import "./closing.css";
import { useState, useRef } from "react";
import { page } from "../page-copy";
import { revealHostProps } from "../reveal-host";

function SubscribeForm() {
  const { close } = page;
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "done">("idle");
  // Spam traps: `company` is a hidden honeypot real users never fill; `startedAt`
  // powers the min-fill-time check. Both are read by the backend, not the UI.
  const [company, setCompany] = useState("");
  const startedAt = useRef(Date.now());
  const valid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const submit = (e: any) => {
    e.preventDefault();
    if (!valid || status === "loading") return;
    setStatus("loading");
    // Failure is treated as success so a backend blip never blocks pre-launch UX.
    fetch("/api/subscribe", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ email, company, elapsed: Date.now() - startedAt.current }),
    })
      .then(() => setStatus("done"))
      .catch(() => setStatus("done"));
  };

  if (status === "done") {
    return (
      <div className="closing-form-slot">
        <div className="closing-done" role="status">
          <span className="closing-done-check" aria-hidden="true">
            ✓
          </span>
          <span className="closing-done-text">{close.done}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="closing-form-slot">
      <form className="closing-form" onSubmit={submit}>
        {/* Honeypot: off-screen, not tabbable, ignored by AT. Bots that autofill
            it get a silent fake-success from the backend. */}
        <input
          type="text"
          name="company"
          className="closing-hp"
          tabIndex={-1}
          autoComplete="off"
          aria-hidden="true"
          value={company}
          onChange={(e: any) => setCompany(e.target.value)}
        />
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder={close.placeholder}
          aria-label="Email address"
          value={email}
          onChange={(e: any) => setEmail(e.target.value)}
        />
        <button
          type="submit"
          className="closing-submit"
          disabled={!valid || status === "loading"}
        >
          {status === "loading" ? close.loading : close.button}
        </button>
      </form>
      <p className="closing-fine">{close.fine}</p>
    </div>
  );
}

export default function Closing({
  delay,
  lines = page.close.lines,
}: {
  delay: number;
  /** Two-line closing couplet above the signup. Defaults to the how-it-works
   *  copy; /philosophy passes its own "Write React. / Ship the Web." bookend
   *  so the CTA is one centered section, not a couplet plus a second block. */
  lines?: ReadonlyArray<{ text: string; tone: string }>;
}) {
  const { close } = page;
  const reveal = revealHostProps(delay);
  return (
    <div
      className={`${reveal.className} closing`}
      style={reveal.style}
      onAnimationEnd={reveal.onAnimationEnd}
    >
      <p className={`closing-line closing-line--${lines[0].tone}`}>
        {lines[0].text}
      </p>
      <p className={`closing-line closing-line--${lines[1].tone}`}>
        {lines[1].text}
      </p>
      <p className="closing-cta">{close.cta}</p>
      <SubscribeForm />
    </div>
  );
}
